import type {
  AgendaItem,
  Document,
  DocumentUploadTypesConfig,
  UploadTypeId,
} from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import {
  EyeIcon,
  LockIcon,
  MoreHorizontal,
  SquareArrowOutUpRight,
} from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import { RoleProtected } from "../common/RoleProtected";
import { AgendaItemSummaryItem } from "../agendaItems/AgendaItemSummaryItem";
import { CappedLinkList } from "../common/CappedLinkList";
import { AppUrlPath } from "@/lib/AppUrlPath";
import {
  createCreatedAtColumn,
  createCreatedByColumn,
  createDescriptionColumn,
  createIdColumn,
  createStatusColumn,
  createTagColumn,
  createTeamColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
  getUsedByFilterFn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { useUserStore } from "@/stores/userStore";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";
import { capitalize, cn } from "@repo/shared/utils";
import { DocumentUploadTypeName } from "./DocumentUploadTypeName";
import { configRepo } from "@/repositories/configRepo";
import { firstValueFrom } from "rxjs";
import { documentRepo } from "@/repositories/documentRepo";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";

export const DocumentNameColumn: ColumnDef<Document> = {
  accessorKey: "name",
  meta: {
    label: "Name",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Name" />
  ),
  cell: ({ row }) => {
    const doc = row.original;
    return (
      <div className="flex flex-row flex-wrap gap-4 items-center">
        {doc.name}
        {doc.confidential && (
          <Tooltip label="Confidential" asChild>
            <LockIcon className="w-4" />
          </Tooltip>
        )}
      </div>
    );
  },
};

export const DocumentConfidentialColumn: ColumnDef<Document> = {
  accessorKey: "confidential",
  meta: {
    label: "Confidential",
    filterVariant: "customBoolean",
    customBooleanOptions: {
      trueLabel: "Confidential",
      falseLabel: "Non-Confidential",
    },
  },
  filterFn: (row, _, value) => {
    const confidential = row.original.confidential ?? false;
    return value === confidential;
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Confidential" />
  ),
  cell: ({ row }) => {
    return <div>{row.original.confidential ? "Confidential" : ""}</div>;
  },
};

export const createDocumentUsedByColumn = (
  tenantId: string
): ColumnDef<Document> => ({
  id: "usedBy",
  accessorKey: "parentIds",
  filterFn: "async",
  meta: {
    label: "Agenda Items Used In",
    filterVariant: "usedBy",
    asyncFilterFn: getUsedByFilterFn<Document, AgendaItem>(
      "parentIds",
      agendaItemRepo,
      tenantId,
      "name"
    ),
  },
  header: ({ column }) => (
    <DataTableColumnHeader
      column={column}
      title="Agenda Items Used In"
      noSort
    />
  ),
  cell: ({ row }) => {
    const document = row.original;

    return (
      <CappedLinkList
        ids={document.parentIds}
        morePath={new AppUrlPath(tenantId).getDocumentPath(document.id)}
      >
        {(agendaItemId) => (
          <AgendaItemSummaryItem
            key={agendaItemId}
            tenantId={tenantId}
            agendaItemId={agendaItemId}
          />
        )}
      </CappedLinkList>
    );
  },
});

export const DocumentUploadTypeColumn: ColumnDef<Document> = {
  accessorKey: "uploadType",
  filterFn: "async",
  meta: {
    label: "Document Type",
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      // cast inputs
      const state = rawState as {
        uploadTypes?: Record<
          UploadTypeId,
          DocumentUploadTypesConfig["uploadTypes"][0]
        >;
        promise?: Promise<void>;
      };
      const filterValue = (rawFilterValue ?? "") as string;

      // wait for config to load (only load once)
      if (!state.promise) {
        state.promise = (async () => {
          // load config
          const { uploadTypes } = await firstValueFrom(
            configRepo.get("documentUploadTypes")
          );

          // map meeting type values to objects
          state.uploadTypes = uploadTypes.reduce(
            (map, type) => ((map[type.id] = type), map),
            Object.create(null)
          );
        })();
      }
      await state.promise;

      // test for match
      if (!filterValue) return true;

      const label =
        state.uploadTypes?.[row.original.uploadType as UploadTypeId]?.label;
      return label?.toLowerCase().includes(filterValue.toLowerCase()) ?? false;
    },
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Document Type" />
  ),
  cell: ({ row }) => <DocumentUploadTypeName type={row.original.uploadType} />,
};

export const DocumentAttachmentsColumn: ColumnDef<Document> = {
  id: "attachmentName",
  accessorFn: (row) => documentRepo.getLinkedAttachment(row)?.name ?? "",
  meta: {
    label: "Attachment Name",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Attachment Name" noSort />
  ),
  sortingFn: "text",
};

export const OrdinanceNumberColumn: ColumnDef<Document> = {
  id: "ordinanceNumber",
  accessorFn: (row) =>
    row.uploadType === "ordinances" ? row.ordinanceData?.number ?? "" : "N/A",
  meta: {
    label: "Ordinance Number",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Ordinance Number" />
  ),
  sortingFn: "text",
};

export const OrdinanceCategoryColumn: ColumnDef<Document> = {
  id: "ordinanceCategory",
  accessorFn: (row) =>
    row.uploadType === "ordinances" ? row.ordinanceData?.category ?? "" : "N/A",
  meta: {
    label: "Ordinance Category",
    filterVariant: "select",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Ordinance Category" />
  ),
  sortingFn: "text",
};

export const OrdinanceStatusColumn: ColumnDef<Document> = {
  id: "ordinanceStatus",
  accessorFn: (row) => row.ordinanceData?.status ?? "proposed",
  meta: {
    label: "Ordinance Status",
    filterVariant: "customSelect",
    customSelectOptions: [
      { label: "Proposed", value: "proposed" },
      { label: "Passed", value: "passed" },
      { label: "Failed", value: "failed" },
    ],
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Ordinance Status" />
  ),
  cell: ({ row }) =>
    row.original.uploadType === "ordinances"
      ? capitalize(row.original.ordinanceData?.status ?? "proposed")
      : "N/A",
  sortingFn: "text",
};

export const ResolutionNumberColumn: ColumnDef<Document> = {
  id: "resolutionNumber",
  accessorFn: (row) =>
    row.uploadType === "resolutions" ? row.resolutionData?.number ?? "" : "N/A",
  meta: {
    label: "Resolution Number",
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Resolution Number" />
  ),
  sortingFn: "text",
};

export const ResolutionStatusColumn: ColumnDef<Document> = {
  id: "resolutionStatus",
  accessorFn: (row) => row.resolutionData?.status ?? "proposed",
  meta: {
    label: "Resolution Status",
    filterVariant: "customSelect",
    customSelectOptions: [
      { label: "Proposed", value: "proposed" },
      { label: "Passed", value: "passed" },
      { label: "Failed", value: "failed" },
    ],
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Resolution Status" />
  ),
  cell: ({ row }) =>
    row.original.uploadType === "resolutions"
      ? capitalize(row.original.resolutionData?.status ?? "proposed")
      : "N/A",
  sortingFn: "text",
};

export const useColumns = ({
  tenantId,
  handleEdit,
  handleArchive,
  handlePreview,
  handleDownload,
  handleView,
}: {
  tenantId: string;
  handleEdit: (id: string) => void;
  handleArchive: (id: string) => void;
  handlePreview: (id: string) => void;
  handleDownload: (document: Document) => void;
  handleView: (id: string) => void;
}) => {
  handleEdit = useFutureCallback(handleEdit);
  handleArchive = useFutureCallback(handleArchive);
  handlePreview = useFutureCallback(handlePreview);
  handleDownload = useFutureCallback(handleDownload);
  handleView = useFutureCallback(handleView);

  const col: ColumnDef<Document>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: function Cell({ row }) {
          const { team, hasMinRole } = useUserStore((s) => ({
            team: s.activeTeam,
            hasMinRole: s.hasMinRole,
          }));
          const isFullOrg = !team || team === "org";

          const doc = row.original;
          const attachment = documentRepo.getLinkedAttachment(doc);

          return (
            <div className="flex flex-row align-middle gap-1 justify-start">
              <Button
                variant="ghost"
                tooltip={isFullOrg ? "Pick a team first" : "Open this document"}
                className={cn(
                  "h-8 w-8 p-0",
                  isFullOrg && "opacity-50 cursor-not-allowed"
                )}
                disabled={isFullOrg}
                onClick={() => handleView(doc.id)}
              >
                <SquareArrowOutUpRight />
              </Button>
              {(!doc.confidential || hasMinRole("edit")) && (
                <Button
                  variant="ghost"
                  tooltip="Preview the document"
                  className="h-8 w-8 p-0"
                  onClick={() => handlePreview(doc.id)}
                >
                  <EyeIcon />
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <RoleProtected minRole="edit">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={isFullOrg ? "Pick a team first" : ""}
                      onClick={() => handleEdit(doc.id)}
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleArchive(doc.id)}>
                      Archive
                    </DropdownMenuItem>
                  </RoleProtected>
                  <RoleProtected minRole={doc.confidential ? "edit" : "view"}>
                    {attachment?.url && !attachment?.isUrl && (
                      <DropdownMenuItem onClick={() => handleDownload(doc)}>
                        Download
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handlePreview(doc.id)}>
                      {attachment?.isUrl ? "Open in New Tab" : "View"}
                    </DropdownMenuItem>
                  </RoleProtected>
                  <DropdownMenuItem
                    onClick={() => navigator.clipboard.writeText(doc.id)}
                  >
                    Copy document ID
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      DocumentNameColumn,
      createTagColumn(),
      createStatusColumn(),
      createDescriptionColumn(),
      DocumentConfidentialColumn,
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
      createDocumentUsedByColumn(tenantId),
      DocumentAttachmentsColumn,
      DocumentUploadTypeColumn,
      OrdinanceNumberColumn,
      OrdinanceCategoryColumn,
      OrdinanceStatusColumn,
      ResolutionNumberColumn,
      ResolutionStatusColumn,
    ],
    [
      tenantId,
      handleEdit,
      handleArchive,
      handlePreview,
      handleDownload,
      handleView,
    ]
  );
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    updatedAt: false,
    usedBy: false,
    ordinanceNumber: false,
    ordinanceCategory: false,
    ordinanceStatus: false,
    resolutionNumber: false,
    resolutionStatus: false,
  },
};
